import { useEffect, useState } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { gql } from "@apollo/client";
import { print } from "@apollo/client/utilities";
import { ApiCourseRequest } from "data/queries/ApiCourse";
import { SchemaData } from "../hooks/types";

type CertificateConfig = {
  title?: string;
  complementaryText?: string;
  workloadHours?: number;
  location?: string;
  orientation?: "PORTRAIT" | "LANDSCAPE";
  backgroundUrl?: string;
  logoUrl?: string;
  legalText?: string;
  signatories?: Array<{ name?: string; role?: string; signatureUrl?: string }>;
  showQrCode?: boolean;
  showValidationCode?: boolean;
};

const formatCertificateDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo" }).format(date);
};

const formatCertificatePeriod = (start?: string, finish?: string) => {
  const startLabel = formatCertificateDate(start);
  const finishLabel = formatCertificateDate(finish);
  return !finish || startLabel === finishLabel ? startLabel : `${startLabel} a ${finishLabel}`;
};

const RESPONSIBLE_UNIT_QUERY = print(gql`
  query CertificatePreviewResponsibleUnit($id: Int!) {
    EducationalUnit(id: $id) { id visibleName name active }
  }
`);

export const CertificatePreview = ({ data }: { data: SchemaData }) => {
  const certificate = ((data as any)?.internalContentJSON?.certificate || {}) as CertificateConfig;
  const landscape = certificate.orientation !== "PORTRAIT";
  const eventName = certificate.title || (data as any)?.title || "Título do evento";
  const eventDate = formatCertificateDate((data as any)?.dateInit);
  const eventPeriod = formatCertificatePeriod((data as any)?.dateInit, (data as any)?.dateFinish);
  const eventLocation = certificate.location || (data as any)?.localization || (data as any)?.address || "Local do evento";
  const responsibleUnitId = Number((data as any)?.educationalUnitId || 0);
  const [responsibleUnitName, setResponsibleUnitName] = useState("");

  useEffect(() => {
    if (!responsibleUnitId) {
      setResponsibleUnitName("");
      return;
    }
    ApiCourseRequest<any>({ query: RESPONSIBLE_UNIT_QUERY, variables: { id: responsibleUnitId } })
      .then((response) => {
        const unit = response.data.data?.EducationalUnit;
        setResponsibleUnitName(unit?.active ? String(unit.visibleName || unit.name || "").trim() : "");
      })
      .catch(() => setResponsibleUnitName(""));
  }, [responsibleUnitId]);

  return (
    <Card sx={{ mt: 2, mb: 3 }}>
      <CardContent>
        <Stack gap={1.5}>
          <Box>
            <Typography variant="h5">Preview do certificado</Typography>
            <Typography variant="caption" color="text.secondary">
              Prévia visual. O nome, as datas e o código serão preenchidos com os dados reais do participante na emissão.
            </Typography>
          </Box>
          <Box sx={{ overflowX: "auto", py: 1 }}>
            <Box
              sx={{
                position: "relative",
                mx: "auto",
                width: landscape ? 760 : 430,
                maxWidth: "100%",
                aspectRatio: landscape ? "297 / 210" : "210 / 297",
                backgroundColor: "#fff",
                backgroundImage: certificate.backgroundUrl ? `url("${certificate.backgroundUrl}")` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: "#222",
                p: { xs: 2, md: 4 },
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              {certificate.logoUrl && (
                <Box component="img" src={certificate.logoUrl} alt="Logo" sx={{ position: "absolute", top: "5%", left: "5.7%", width: "16.6%", height: "12.6%", objectFit: "contain" }} />
              )}
              <Typography sx={{ color: "#25387b", fontSize: { xs: 20, md: 30 }, fontWeight: 700 }}>CERTIFICADO</Typography>
              <Typography sx={{ mt: 2, fontSize: { xs: 10, md: 14 } }}>Certificamos que</Typography>
              <Typography sx={{ my: 1, fontSize: { xs: 17, md: 25 }, fontWeight: 700 }}>NOME DO PARTICIPANTE</Typography>
              <Typography sx={{ fontSize: { xs: 10, md: 14 } }}>
                participou e concluiu {eventName},<br />
                promovido por {eventLocation}, com carga horária de {certificate.workloadHours || 0} horas<br />
                realizada no período de {eventPeriod}.
              </Typography>
              {certificate.complementaryText && <Typography sx={{ mt: 1, fontSize: { xs: 8, md: 11 }, whiteSpace: "pre-line" }}>{certificate.complementaryText}</Typography>}
              <Stack direction="row" justifyContent="space-around" alignItems="flex-end" sx={{ width: "100%", mt: "auto", mb: 2 }}>
                {(certificate.signatories || []).slice(0, 3).map((signer, index) => (
                  <Box key={`${signer.name}-${index}`} sx={{ width: "28%", pt: 0.5 }}>
                    <Box sx={{ height: 48, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                      {signer.signatureUrl && <Box component="img" src={signer.signatureUrl} alt={`Assinatura de ${signer.name || "signatário"}`} sx={{ maxWidth: "100%", maxHeight: 46, objectFit: "contain" }} />}
                    </Box>
                    <Box sx={{ borderTop: "1px solid #555", pt: 0.5 }}>
                    <Typography sx={{ fontSize: { xs: 7, md: 9 } }}>{signer.name || "Signatário"}<br />{signer.role || "Cargo"}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
              {certificate.legalText && <Typography sx={{ position: "absolute", width: "45%", left: "29%", bottom: 15, fontSize: { xs: 5, md: 7 }, lineHeight: 1.2 }}>{certificate.legalText}</Typography>}
              <Box sx={{ position: "absolute", right: "8%", bottom: "25%", width: "21%", textAlign: "center" }}>
                <Typography sx={{ fontSize: { xs: 8, md: 11 }, lineHeight: 1.45 }}>
                  {eventDate}<br />{responsibleUnitName || "Unidade responsável"}
                </Typography>
              </Box>
              <Box sx={{ position: "absolute", top: 8, right: 5, bottom: 8, overflow: "hidden", display: "flex", alignItems: "flex-end" }}>
                <Typography sx={{ fontSize: { xs: 5, md: 7 }, lineHeight: 1, whiteSpace: "nowrap", writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                  Número e data de emissão{certificate.showValidationCode !== false ? " | Código de validação" : ""}
                </Typography>
              </Box>
              {certificate.showQrCode !== false && <Box sx={{ position: "absolute", right: 14, bottom: 12, width: 42, height: 42, bgcolor: "#fff", border: "5px dotted #222" }} />}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
